import { useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import type { ReactNode } from "react";
import type { Deployment } from "@/features/deployment-selection/model/deployment";
import {
  clearRuntimeSession,
  setRuntimeSession,
} from "@/features/runtime-session/api/runtime-http-client";
import { useRuntimeSession } from "@/features/runtime-session/hooks/use-runtime-session";
import { buildRequestedOperations } from "@/features/runtime-session/model/operation-catalog";

interface RuntimeSessionProviderProps {
  deploymentId: string;
  deployment: Deployment;
  children: ReactNode;
}

/**
 * Keeps the application-wide Runtime API client synchronized with the selected
 * deployment's current short-lived runtime session. Rendering is never gated
 * while a session is loading or unavailable.
 */
export function RuntimeSessionProvider({
  deploymentId,
  deployment,
  children,
}: RuntimeSessionProviderProps) {
  const operationIds = useMemo(() => buildRequestedOperations(deployment), [deployment]);
  const { data: runtimeSession, refetch } = useRuntimeSession(deploymentId, operationIds);
  const refreshRuntimeSession = useCallback(async () => {
    const result = await refetch();
    if (result.data?.deployment_id === deploymentId) return result.data;

    throw result.error ?? new Error("Unable to refresh the runtime session");
  }, [deploymentId, refetch]);

  useLayoutEffect(() => {
    if (runtimeSession?.deployment_id === deploymentId) {
      setRuntimeSession(runtimeSession, refreshRuntimeSession);
    } else {
      clearRuntimeSession();
    }
  }, [deploymentId, refreshRuntimeSession, runtimeSession]);

  useEffect(
    () => () => {
      clearRuntimeSession(deploymentId);
    },
    [deploymentId]
  );

  return children;
}
